import { useCallback, useMemo, useReducer } from "react";
import type { Dispatch, SetStateAction } from "react";
import type {
  AddEntryResult,
  BiodiversityData,
  DocumentMetadata,
  ElectricityEntry,
  FuelEntry,
  PollutionData,
  RefrigerantEntry,
  RiskManagementData,
  TransitionMeasure,
  WasteData,
  WaterData,
} from "./types";
import {
  calculateElectricityEmissions as calculateElectricityEmissionsApi,
  calculateFuelEmissions as calculateFuelEmissionsApi,
  calculateRefrigerantEmissions as calculateRefrigerantEmissionsApi,
} from "@/service/emissions";

export const DEFAULT_FUEL_ENTRY: Partial<FuelEntry> = {
  activityType: "stationary",
  fuelType: "diesel",
  period: null,
  unit: "liters",
  quantity: undefined,
};

export const DEFAULT_ELECTRICITY_ENTRY: Partial<ElectricityEntry> = {
  period: null,
  unit: "kwh",
  meterId: "",
  gridPercent: 100,
  selfGeneratedPercent: 0,
  isRenewable: false,
  renewableSource: "solar",
};

export const DEFAULT_REFRIGERANT_ENTRY: Partial<RefrigerantEntry> = {
  refrigerantType: "",
  unit: "kg",
  period: null,
};

type EnvironmentState = {
  fuelEntries: FuelEntry[];
  electricityEntries: ElectricityEntry[];
  refrigerantEntries: RefrigerantEntry[];
  carbonSupportingDocuments: DocumentMetadata[];
  currentFuelEntry: Partial<FuelEntry>;
  currentElectricityEntry: Partial<ElectricityEntry>;
  currentRefrigerantEntry: Partial<RefrigerantEntry>;
  pollutionData: PollutionData;
  biodiversityData: BiodiversityData;
  biodiversitySupportingDocuments: DocumentMetadata[];
  waterData: WaterData;
  waterSupportingDocuments: DocumentMetadata[];
  wasteData: WasteData;
  wasteSupportingDocuments: DocumentMetadata[];
  riskManagementData: RiskManagementData;
  transitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
};

type EnvironmentAction =
  | { type: "setFuelEntries"; payload: SetStateAction<FuelEntry[]> }
  | { type: "setElectricityEntries"; payload: SetStateAction<ElectricityEntry[]> }
  | { type: "setRefrigerantEntries"; payload: SetStateAction<RefrigerantEntry[]> }
  | { type: "setCarbonSupportingDocuments"; payload: SetStateAction<DocumentMetadata[]> }
  | { type: "setCurrentFuelEntry"; payload: SetStateAction<Partial<FuelEntry>> }
  | { type: "setCurrentElectricityEntry"; payload: SetStateAction<Partial<ElectricityEntry>> }
  | { type: "setCurrentRefrigerantEntry"; payload: SetStateAction<Partial<RefrigerantEntry>> }
  | { type: "setPollutionData"; payload: SetStateAction<PollutionData> }
  | { type: "updatePollutionData"; payload: Partial<PollutionData> }
  | { type: "setBiodiversityData"; payload: SetStateAction<BiodiversityData> }
  | { type: "updateBiodiversityData"; payload: Partial<BiodiversityData> }
  | { type: "setBiodiversitySupportingDocuments"; payload: SetStateAction<DocumentMetadata[]> }
  | { type: "setWaterData"; payload: SetStateAction<WaterData> }
  | { type: "updateWaterData"; payload: Partial<WaterData> }
  | { type: "setWaterSupportingDocuments"; payload: SetStateAction<DocumentMetadata[]> }
  | { type: "setWasteData"; payload: SetStateAction<WasteData> }
  | { type: "updateWasteData"; payload: Partial<WasteData> }
  | { type: "setWasteSupportingDocuments"; payload: SetStateAction<DocumentMetadata[]> }
  | { type: "addFuelEntry"; payload: FuelEntry }
  | { type: "updateFuelEntry"; payload: { id: string; updates: Partial<FuelEntry> } }
  | { type: "deleteFuelEntry"; payload: string }
  | { type: "addElectricityEntry"; payload: ElectricityEntry }
  | { type: "updateElectricityEntry"; payload: { id: string; updates: Partial<ElectricityEntry> } }
  | { type: "deleteElectricityEntry"; payload: string }
  | { type: "addRefrigerantEntry"; payload: RefrigerantEntry }
  | { type: "updateRefrigerantEntry"; payload: { id: string; updates: Partial<RefrigerantEntry> } }
  | { type: "deleteRefrigerantEntry"; payload: string }
  | { type: "setRiskManagementData"; payload: SetStateAction<RiskManagementData> }
  | { type: "updateRiskManagementData"; payload: Partial<RiskManagementData> }
  | { type: "setTransitionMeasuresForTopic"; payload: { topicId: string; measures: TransitionMeasure[] } };

const initialEnvironmentState: EnvironmentState = {
  fuelEntries: [],
  electricityEntries: [],
  refrigerantEntries: [],
  carbonSupportingDocuments: [],
  currentFuelEntry: {
    activityType: "stationary",
    fuelType: "diesel",
    period: null,
    unit: "liters",
  },
  currentElectricityEntry: {
    period: null,
    unit: "kwh",
    meterId: "",
    gridPercent: 100,
    selfGeneratedPercent: 0,
    isRenewable: false,
    renewableSource: "solar",
  },
  currentRefrigerantEntry: {
    refrigerantType: "",
    unit: "kg",
    period: null,
  },
  pollutionData: {
    applicability: null,
    reportingReason: null,
    notes: "",
    publishedDataLink: "",
    pollutants: [],
  },
  biodiversityData: {
    sitesInSensitiveAreas: null,
    areaOfSensitiveSites: null,
    totalLandUse: null,
    sealedArea: null,
    natureOrientedOnSite: null,
    natureOrientedOffSite: null,
    notes: "",
    evidenceUrl: "",
  },
  biodiversitySupportingDocuments: [],
  waterData: {
    totalWithdrawal: null,
    withdrawalInStressAreas: null,
    hasConsumptionProcess: null,
    annualConsumption: null,
    waterSource: "",
    notes: "",
    evidenceUrl: "",
  },
  waterSupportingDocuments: [],
  wasteData: {
    appliesCircularEconomy: false,
    circularEconomyPrinciples: [],
    circularEconomyDescription: "",
    nonHazardousWaste: null,
    nonHazardousWasteUnit: "kg",
    hazardousWaste: null,
    hazardousWasteUnit: "kg",
    wasteDiverted: null,
    wasteDivertedUnit: "kg",
    tracksMaterials: null,
    materialsDescription: "",
    notes: "",
    evidenceUrl: "",
  },
  wasteSupportingDocuments: [],
  riskManagementData: {
    climateRisksIdentified: null,
    mainHazards: "",
    riskAssessmentProcess: "",
    timeHorizon: [],
    adaptationActions: "",
    riskLevel: null,
    potentialEffects: "",
  },
  transitionMeasuresByTopic: {},
};

function resolveSetState<T>(value: SetStateAction<T>, current: T): T {
  return typeof value === "function" ? (value as (prev: T) => T)(current) : value;
}

function environmentReducer(state: EnvironmentState, action: EnvironmentAction): EnvironmentState {
  switch (action.type) {
    case "setFuelEntries":
      return { ...state, fuelEntries: resolveSetState(action.payload, state.fuelEntries) };
    case "setElectricityEntries":
      return { ...state, electricityEntries: resolveSetState(action.payload, state.electricityEntries) };
    case "setRefrigerantEntries":
      return { ...state, refrigerantEntries: resolveSetState(action.payload, state.refrigerantEntries) };
    case "setCarbonSupportingDocuments":
      return { ...state, carbonSupportingDocuments: resolveSetState(action.payload, state.carbonSupportingDocuments) };
    case "setCurrentFuelEntry":
      return { ...state, currentFuelEntry: resolveSetState(action.payload, state.currentFuelEntry) };
    case "setCurrentElectricityEntry":
      return { ...state, currentElectricityEntry: resolveSetState(action.payload, state.currentElectricityEntry) };
    case "setCurrentRefrigerantEntry":
      return { ...state, currentRefrigerantEntry: resolveSetState(action.payload, state.currentRefrigerantEntry) };
    case "setPollutionData":
      return { ...state, pollutionData: resolveSetState(action.payload, state.pollutionData) };
    case "updatePollutionData":
      return { ...state, pollutionData: { ...state.pollutionData, ...action.payload } };
    case "setBiodiversityData":
      return { ...state, biodiversityData: resolveSetState(action.payload, state.biodiversityData) };
    case "updateBiodiversityData":
      return { ...state, biodiversityData: { ...state.biodiversityData, ...action.payload } };
    case "setBiodiversitySupportingDocuments":
      return {
        ...state,
        biodiversitySupportingDocuments: resolveSetState(action.payload, state.biodiversitySupportingDocuments),
      };
    case "setWaterData":
      return { ...state, waterData: resolveSetState(action.payload, state.waterData) };
    case "updateWaterData":
      return { ...state, waterData: { ...state.waterData, ...action.payload } };
    case "setWaterSupportingDocuments":
      return { ...state, waterSupportingDocuments: resolveSetState(action.payload, state.waterSupportingDocuments) };
    case "setWasteData":
      return { ...state, wasteData: resolveSetState(action.payload, state.wasteData) };
    case "updateWasteData":
      return { ...state, wasteData: { ...state.wasteData, ...action.payload } };
    case "setWasteSupportingDocuments":
      return { ...state, wasteSupportingDocuments: resolveSetState(action.payload, state.wasteSupportingDocuments) };
    case "addFuelEntry":
      return { ...state, fuelEntries: [...state.fuelEntries, action.payload] };
    case "updateFuelEntry":
      return {
        ...state,
        fuelEntries: state.fuelEntries.map(entry =>
          entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
        ),
      };
    case "deleteFuelEntry":
      return { ...state, fuelEntries: state.fuelEntries.filter(entry => entry.id !== action.payload) };
    case "addElectricityEntry":
      return { ...state, electricityEntries: [...state.electricityEntries, action.payload] };
    case "updateElectricityEntry":
      return {
        ...state,
        electricityEntries: state.electricityEntries.map(entry =>
          entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
        ),
      };
    case "deleteElectricityEntry":
      return { ...state, electricityEntries: state.electricityEntries.filter(entry => entry.id !== action.payload) };
    case "addRefrigerantEntry":
      return { ...state, refrigerantEntries: [...state.refrigerantEntries, action.payload] };
    case "updateRefrigerantEntry":
      return {
        ...state,
        refrigerantEntries: state.refrigerantEntries.map(entry =>
          entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
        ),
      };
    case "deleteRefrigerantEntry":
      return { ...state, refrigerantEntries: state.refrigerantEntries.filter(entry => entry.id !== action.payload) };
    case "setRiskManagementData":
      return { ...state, riskManagementData: resolveSetState(action.payload, state.riskManagementData) };
    case "updateRiskManagementData":
      return { ...state, riskManagementData: { ...state.riskManagementData, ...action.payload } };
    case "setTransitionMeasuresForTopic":
      return {
        ...state,
        transitionMeasuresByTopic: { ...state.transitionMeasuresByTopic, [action.payload.topicId]: action.payload.measures },
      };
    default:
      return state;
  }
}

type EnvironmentActions = {
  setFuelEntries: Dispatch<SetStateAction<FuelEntry[]>>;
  setElectricityEntries: Dispatch<SetStateAction<ElectricityEntry[]>>;
  setRefrigerantEntries: Dispatch<SetStateAction<RefrigerantEntry[]>>;
  setCarbonSupportingDocuments: Dispatch<SetStateAction<DocumentMetadata[]>>;
  setCurrentFuelEntry: Dispatch<SetStateAction<Partial<FuelEntry>>>;
  setCurrentElectricityEntry: Dispatch<SetStateAction<Partial<ElectricityEntry>>>;
  setCurrentRefrigerantEntry: Dispatch<SetStateAction<Partial<RefrigerantEntry>>>;
  calculateFuelEmissions: (entry: Partial<FuelEntry>) => Promise<{ tco2e: number | null; energyMwh: number | null }>;
  calculateElectricityEmissions: (entry: Partial<ElectricityEntry>) => Promise<number | null>;
  addFuelEntry: () => Promise<AddEntryResult>;
  addElectricityEntry: () => Promise<AddEntryResult>;
  addRefrigerantEntry: () => Promise<AddEntryResult>;
  deleteFuelEntry: (id: string) => void;
  deleteElectricityEntry: (id: string) => void;
  deleteRefrigerantEntry: (id: string) => void;
  updatePollutionData: (updates: Partial<PollutionData>) => void;
  setPollutionData: Dispatch<SetStateAction<PollutionData>>;
  updateBiodiversityData: (updates: Partial<BiodiversityData>) => void;
  setBiodiversityData: Dispatch<SetStateAction<BiodiversityData>>;
  setBiodiversitySupportingDocuments: Dispatch<SetStateAction<DocumentMetadata[]>>;
  updateWaterData: (updates: Partial<WaterData>) => void;
  setWaterData: Dispatch<SetStateAction<WaterData>>;
  setWaterSupportingDocuments: Dispatch<SetStateAction<DocumentMetadata[]>>;
  updateWasteData: (updates: Partial<WasteData>) => void;
  setWasteData: Dispatch<SetStateAction<WasteData>>;
  setWasteSupportingDocuments: Dispatch<SetStateAction<DocumentMetadata[]>>;
  setRiskManagementData: Dispatch<SetStateAction<RiskManagementData>>;
  updateRiskManagementData: (updates: Partial<RiskManagementData>) => void;
  setTransitionMeasuresForTopic: (topicId: string, measures: TransitionMeasure[]) => void;
  updateFuelEntry: (id: string, updates: Partial<FuelEntry>) => void;
  updateElectricityEntry: (id: string, updates: Partial<ElectricityEntry>) => void;
  updateRefrigerantEntry: (id: string, updates: Partial<RefrigerantEntry>) => void;
};

export function useEnvironmentState(options?: { country?: string }): { state: EnvironmentState; actions: EnvironmentActions } {
  const { country } = options ?? {};
  const [state, dispatch] = useReducer(environmentReducer, initialEnvironmentState);

  const calculateFuelEmissions = useCallback(
    async (entry: Partial<FuelEntry>): Promise<{ tco2e: number | null; energyMwh: number | null }> => calculateFuelEmissionsApi(entry),
    []
  );

  const calculateElectricityEmissions = useCallback(
    async (entry: Partial<ElectricityEntry>): Promise<number | null> => calculateElectricityEmissionsApi(entry, country),
    [country]
  );

  const addFuelEntry = useCallback(async (): Promise<AddEntryResult> => {
    const qty = state.currentFuelEntry.quantity;
    const period = state.currentFuelEntry.period;

    if (!period) {
      return { ok: false, error: "Please select a period" };
    }
    if (qty === undefined || qty === null || qty <= 0) {
      return { ok: false, error: "Please enter a quantity greater than zero" };
    }

    if (state.currentFuelEntry.activityType === "mixed") {
      const statPct = state.currentFuelEntry.allocationStationaryPct;
      const mobPct = state.currentFuelEntry.allocationMobilePct;
      if (statPct == null || mobPct == null) {
        return { ok: false, error: "Both stationary and mobile percentages are required for mixed-use" };
      }
      if (statPct < 0 || mobPct < 0 || statPct > 100 || mobPct > 100) {
        return { ok: false, error: "Percentages must be between 0 and 100" };
      }
      if (Math.round(statPct + mobPct) !== 100) {
        return { ok: false, error: "Stationary and mobile percentages must total exactly 100%" };
      }
    }

    const newEntry: FuelEntry = {
      id: `fuel-${Date.now()}`,
      activityType: state.currentFuelEntry.activityType || "stationary",
      fuelType: state.currentFuelEntry.fuelType || "diesel",
      period: state.currentFuelEntry.period ?? null,
      quantity: qty,
      unit: state.currentFuelEntry.unit || "liters",
      allocationStationaryPct: state.currentFuelEntry.allocationStationaryPct,
      allocationMobilePct: state.currentFuelEntry.allocationMobilePct,
      evidenceId: state.currentFuelEntry.evidenceId,
      evidenceName: state.currentFuelEntry.evidenceName,
      tco2e: null,
      energyMwh: undefined,
      calculating: true,
    };

    dispatch({ type: "addFuelEntry", payload: newEntry });

    const result = await calculateFuelEmissions(newEntry);
    dispatch({
      type: "updateFuelEntry",
      payload: {
        id: newEntry.id,
        updates: { tco2e: result.tco2e, energyMwh: result.energyMwh ?? undefined, calculating: false },
      },
    });

    dispatch({
      type: "setCurrentFuelEntry",
      payload: {
        activityType: "stationary",
        fuelType: "diesel",
        period: null,
        unit: "liters",
      },
    });

    return { ok: true, tco2e: result.tco2e };
  }, [calculateFuelEmissions, state.currentFuelEntry]);

  const addElectricityEntry = useCallback(async (): Promise<AddEntryResult> => {
    if (!state.currentElectricityEntry.consumption || state.currentElectricityEntry.consumption <= 0 || !state.currentElectricityEntry.period) {
      return { ok: false, error: "Please enter consumption and period" };
    }

    // Validate percentages sum to 100
    const gridPct = state.currentElectricityEntry.gridPercent ?? 100;
    const selfPct = state.currentElectricityEntry.selfGeneratedPercent ?? 0;
    if (Math.abs(gridPct + selfPct - 100) > 0.01) {
      return { ok: false, error: "Grid and Self-Generated percentages must sum to 100%" };
    }

    // Calculate kWh split
    const consumptionKwh = state.currentElectricityEntry.unit === "mwh"
      ? state.currentElectricityEntry.consumption * 1000
      : state.currentElectricityEntry.consumption;
    const gridKwh = consumptionKwh * (gridPct / 100);
    const selfGeneratedKwh = consumptionKwh * (selfPct / 100);

    const newEntry: ElectricityEntry = {
      id: `elec-${Date.now()}`,
      period: state.currentElectricityEntry.period ?? null,
      consumption: state.currentElectricityEntry.consumption,
      unit: state.currentElectricityEntry.unit || "kwh",
      meterId: state.currentElectricityEntry.meterId,
      // Grid vs Self-Generated split
      gridPercent: gridPct,
      selfGeneratedPercent: selfPct,
      isRenewable: state.currentElectricityEntry.isRenewable ?? false,
      renewableSource: state.currentElectricityEntry.renewableSource,
      gridKwh,
      selfGeneratedKwh,
      // Evidence
      evidenceId: state.currentElectricityEntry.evidenceId,
      evidenceName: state.currentElectricityEntry.evidenceName,
      tco2e: null,
      calculating: true,
    };

    dispatch({ type: "addElectricityEntry", payload: newEntry });

    const tco2e = await calculateElectricityEmissions(newEntry);
    dispatch({ type: "updateElectricityEntry", payload: { id: newEntry.id, updates: { tco2e, calculating: false } } });

    dispatch({
      type: "setCurrentElectricityEntry",
      payload: {
        period: null,
        unit: "kwh",
        meterId: "",
        gridPercent: 100,
        selfGeneratedPercent: 0,
        isRenewable: false,
      },
    });

    return { ok: true, tco2e };
  }, [calculateElectricityEmissions, state.currentElectricityEntry]);

  const addRefrigerantEntry = useCallback(async (): Promise<AddEntryResult> => {
    if (!state.currentRefrigerantEntry.refrigerantType || !state.currentRefrigerantEntry.leakedAmount || state.currentRefrigerantEntry.leakedAmount <= 0) {
      return { ok: false, error: "Please enter refrigerant type and leaked amount" };
    }

    if (!state.currentRefrigerantEntry.period) {
      return { ok: false, error: "Please select a period" };
    }

    const newEntry: RefrigerantEntry = {
      id: `refrigerant-${Date.now()}`,
      refrigerantType: state.currentRefrigerantEntry.refrigerantType,
      leakedAmount: state.currentRefrigerantEntry.leakedAmount,
      unit: state.currentRefrigerantEntry.unit || "kg",
      period: state.currentRefrigerantEntry.period ?? null,
      evidenceId: state.currentRefrigerantEntry.evidenceId,
      evidenceName: state.currentRefrigerantEntry.evidenceName,
      tco2e: null,
      calculating: true,
    };

    dispatch({ type: "addRefrigerantEntry", payload: newEntry });

    const tco2e = await calculateRefrigerantEmissionsApi(newEntry);
    dispatch({ type: "updateRefrigerantEntry", payload: { id: newEntry.id, updates: { tco2e, calculating: false } } });

    dispatch({
      type: "setCurrentRefrigerantEntry",
      payload: {
        refrigerantType: "",
        unit: "kg",
        period: null,
      },
    });

    return { ok: true, tco2e };
  }, [state.currentRefrigerantEntry]);

  const actions = useMemo<EnvironmentActions>(
    () => ({
      setFuelEntries: (value) => dispatch({ type: "setFuelEntries", payload: value }),
      setElectricityEntries: (value) => dispatch({ type: "setElectricityEntries", payload: value }),
      setRefrigerantEntries: (value) => dispatch({ type: "setRefrigerantEntries", payload: value }),
      setCarbonSupportingDocuments: (value) => dispatch({ type: "setCarbonSupportingDocuments", payload: value }),
      setCurrentFuelEntry: (value) => dispatch({ type: "setCurrentFuelEntry", payload: value }),
      setCurrentElectricityEntry: (value) => dispatch({ type: "setCurrentElectricityEntry", payload: value }),
      setCurrentRefrigerantEntry: (value) => dispatch({ type: "setCurrentRefrigerantEntry", payload: value }),
      calculateFuelEmissions,
      calculateElectricityEmissions,
      addFuelEntry,
      addElectricityEntry,
      addRefrigerantEntry,
      deleteFuelEntry: (id) => dispatch({ type: "deleteFuelEntry", payload: id }),
      deleteElectricityEntry: (id) => dispatch({ type: "deleteElectricityEntry", payload: id }),
      deleteRefrigerantEntry: (id) => dispatch({ type: "deleteRefrigerantEntry", payload: id }),
      updatePollutionData: (updates) => dispatch({ type: "updatePollutionData", payload: updates }),
      setPollutionData: (value) => dispatch({ type: "setPollutionData", payload: value }),
      updateBiodiversityData: (updates) => dispatch({ type: "updateBiodiversityData", payload: updates }),
      setBiodiversityData: (value) => dispatch({ type: "setBiodiversityData", payload: value }),
      setBiodiversitySupportingDocuments: (value) =>
        dispatch({ type: "setBiodiversitySupportingDocuments", payload: value }),
      updateWaterData: (updates) => dispatch({ type: "updateWaterData", payload: updates }),
      setWaterData: (value) => dispatch({ type: "setWaterData", payload: value }),
      setWaterSupportingDocuments: (value) => dispatch({ type: "setWaterSupportingDocuments", payload: value }),
      updateWasteData: (updates) => dispatch({ type: "updateWasteData", payload: updates }),
      setWasteData: (value) => dispatch({ type: "setWasteData", payload: value }),
      setWasteSupportingDocuments: (value) => dispatch({ type: "setWasteSupportingDocuments", payload: value }),
      setRiskManagementData: (value) => dispatch({ type: "setRiskManagementData", payload: value }),
      updateRiskManagementData: (updates) => dispatch({ type: "updateRiskManagementData", payload: updates }),
      setTransitionMeasuresForTopic: (topicId, measures) =>
        dispatch({ type: "setTransitionMeasuresForTopic", payload: { topicId, measures } }),
      updateFuelEntry: (id, updates) => dispatch({ type: "updateFuelEntry", payload: { id, updates } }),
      updateElectricityEntry: (id, updates) => dispatch({ type: "updateElectricityEntry", payload: { id, updates } }),
      updateRefrigerantEntry: (id, updates) => dispatch({ type: "updateRefrigerantEntry", payload: { id, updates } }),
    }),
    [
      addElectricityEntry,
      addFuelEntry,
      addRefrigerantEntry,
      calculateElectricityEmissions,
      calculateFuelEmissions,
    ]
  );

  return { state, actions };
}
